import { usePerformLogout } from '@hooks/useAuth';
import LogoutConfirmModal from '@modules/merchant/layout/LogoutConfirmModal';
import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';

const LogoutConfirmContext = createContext(null);

export const useLogoutConfirm = () => {
  const context = useContext(LogoutConfirmContext);

  if (!context) {
    throw new Error('useLogoutConfirm must be used within LogoutConfirmProvider');
  }

  return context;
};

const LogoutConfirmProvider = memo(({ children }) => {
  const performLogout = usePerformLogout();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const requestLogout = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setOpen(false);
  }, [submitting]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      await performLogout();
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, [performLogout]);

  const value = useMemo(() => ({ requestLogout }), [requestLogout]);

  return (
    <LogoutConfirmContext.Provider value={value}>
      {children}
      <LogoutConfirmModal open={open} onClose={handleClose} onConfirm={handleConfirm} />
    </LogoutConfirmContext.Provider>
  );
});

LogoutConfirmProvider.displayName = 'LogoutConfirmProvider';

export default LogoutConfirmProvider;
